import { DataSource } from '@angular/cdk/table';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { Sort, SortDirection } from '@angular/material/sort';
import { Router } from '@angular/router';
import { StatusBadgeTone } from '../status-badge/status-badge.component';

export type TableColumnType =
  | 'text'
  | 'textList'
  | 'link'
  | 'links'
  | 'icons'
  | 'iconText'
  | 'status'
  | 'statusIcon'
  | 'favourite'
  | 'actionGroup';

export type TableStatusTone = StatusBadgeTone;

export type TableStatus = {
  label: string;
  tone: TableStatusTone;
  icon?: string | null; // used by 'statusIcon' columns
};

export type TableIcon = {
  iconName: string;
  tooltip?: string;
  ariaLabel?: string;
};

export type TableLink = {
  label: string;
  href: string;
  target?: string;
};

export type TableButton = {
  actionId?: string;
  label: string;
  disabled?: boolean;
  ariaLabel?: string;
  cssClass?: string;
  color?: 'primary' | 'accent' | 'warn';
  variant?: 'stroked' | 'raised';
};

export type TableActionGroup = {
  link?: TableLink | null;
  button?: TableButton | null;
  secondaryButton?: TableButton | null;
  tertiaryButton?: TableButton | null;
  menuItems?: TableButton[];
  menuAriaLabel?: string;
};

export type TableColumn<T = any> = {
  id: string;
  header: string;
  showHeader: boolean;
  type: TableColumnType;
  field?: string;
  text?: (row: T) => string;
  textList?: (row: T) => string[];
  link?: (row: T) => TableLink | null;
  links?: (row: T) => TableLink[];
  icons?: (row: T) => TableIcon[];
  icon?: (row: T) => TableIcon | null;
  status?: (row: T) => TableStatus | null;
  showFavourite?: (row: T) => boolean;
  isFavourite?: (row: T) => boolean;
  favouriteDisabled?: (row: T) => boolean;
  favouriteAriaLabel?: (row: T) => string;
  actions?: (row: T) => TableActionGroup;
  align?: 'left' | 'center' | 'right';
  columnWidth?: number | null; // in pixels
  sortable?: boolean;
  sortId?: string;
};

let nextTableInstanceId = 0;

@Component({
  standalone: false,
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
})
export class TableComponent<T = any> {
  private readonly pageWindowSize = 5;
  private readonly instanceId = nextTableInstanceId++;

  @Input() dataSource?: DataSource<T> | T[];
  @Input() columns: TableColumn<T>[] = [];
  @Input() ariaLabel: string = '';
  @Input() heading: string = '';
  @Input() headingId: string = '';
  @Input() noDataText: string = '';
  @Input() length: number = 0;
  @Input() pageSize: number = 10;
  @Input() pageIndex: number = 0;
  @Input() pageSizeOptions: number[] = [10, 20, 50];
  @Input() showTopSummary: boolean = false;
  @Input() topSummaryText: string = '';
  @Input() showRangeSummary: boolean = true;
  @Input() showPagination: boolean = true;
  @Input() resultNoun: string = 'results'; // the 'results' word can be overwritten depending on the table
  @Input() rowHeight: number = 72; // in pixels
  @Input() headerRowHeight: number | null = null; // in pixels
  @Input() rowId: (row: T) => number | string = (row: any) => row?.id;
  @Input() sortActive: string = '';
  @Input() sortDirection: SortDirection = '';
  @Input() selectableRows: boolean = false;
  @Input() visibleRows: T[] = [];
  @Input() selectedRowIds: ReadonlySet<number | string> = new Set<number | string>();
  @Input() rowSelectable: (row: T) => boolean = () => true;
  @Input() selectionNoun: string = 'item';
  @Input() selectionActionLabel: string = '';
  @Input() selectionDisabled: boolean = false;
  @Input() selectionIdPrefix: string = 'table-selection';
  @Input() rowHover: boolean = true;

  @Output() pageChanged = new EventEmitter<PageEvent>();
  @Output() sortChanged = new EventEmitter<Sort>();
  @Output() rowClicked = new EventEmitter<T>();
  @Output() favouriteToggle = new EventEmitter<{ row: T; event: Event }>();
  @Output() actionButtonClick = new EventEmitter<{ row: T; columnId: string; actionId?: string }>();
  @Output() rowSelectionChanged = new EventEmitter<{ row: T; selected: boolean }>();
  @Output() visibleSelectionChanged = new EventEmitter<{ rows: T[]; selected: boolean }>();
  @Output() selectionCleared = new EventEmitter<void>();
  @Output() selectionAction = new EventEmitter<void>();

  hoveredRowId: number | string | null = null;
  headerHovered: boolean = false;

  constructor(private readonly router: Router) {}

  /** Keeps same-app table links as real anchors while still navigating within the SPA. */
  onLinkClick(event: MouseEvent, link: TableLink): void {
    event.stopPropagation();

    const isModifiedClick =
      event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey;
    const isInAppRoute = !!link.href?.startsWith('/');

    if (isModifiedClick || this.linkOpensNewTab(link) || !isInAppRoute) {
      return;
    }

    event.preventDefault();
    this.router.navigateByUrl(link.href);
  }

  linkOpensNewTab(link: TableLink): boolean {
    return !!link.target && link.target !== '_self';
  }

  linkRel(link: TableLink): string | null {
    return this.linkOpensNewTab(link) ? 'noopener noreferrer' : null;
  }

  linkAriaLabel(link: TableLink): string | null {
    return this.linkOpensNewTab(link) ? `${link.label} (opens in a new tab)` : null;
  }

  get displayedColumnIds(): string[] {
    const columnIds = this.columns.map((column) => column.id);
    return this.selectableRows ? ['tableSelection', ...columnIds] : columnIds;
  }

  get headingElementId(): string {
    return this.headingId || `table-heading-${this.instanceId}`;
  }

  get pageSizeSelectId(): string {
    return `table-page-size-select-${this.instanceId}`;
  }

  get paginationLabel(): string {
    return this.ariaLabel ? `${this.ariaLabel} pagination` : 'Pagination';
  }

  get selectedRowCount(): number {
    return this.selectedRowIds.size;
  }

  get selectionModeActive(): boolean {
    return this.selectedRowCount > 0;
  }

  get selectableVisibleRows(): T[] {
    return this.visibleRows.filter((row) => this.rowSelectable(row));
  }

  get allVisibleRowsSelected(): boolean {
    const rows = this.selectableVisibleRows;
    return rows.length > 0 && rows.every((row) => this.selectedRowIds.has(this.rowId(row)));
  }

  get selectionSummary(): string {
    const noun = this.selectedRowCount === 1 ? this.selectionNoun : `${this.selectionNoun}s`;
    return `${this.selectedRowCount} ${noun} selected`;
  }

  get rangeStart(): number {
    if (this.length === 0) {
      return 0;
    }
    return this.pageIndex * this.pageSize + 1;
  }

  get rangeEnd(): number {
    if (this.length === 0) {
      return 0;
    }
    return Math.min((this.pageIndex + 1) * this.pageSize, this.length);
  }

  get totalPages(): number {
    if (this.length <= 0 || this.pageSize <= 0) {
      return 0;
    }

    return Math.ceil(this.length / this.pageSize);
  }

  get currentPage(): number {
    if (this.totalPages === 0) {
      return 0;
    }

    return this.pageIndex + 1;
  }

  get pageWindowStart(): number {
    if (this.currentPage <= 0) {
      return 1;
    }

    const maxWindowStart = Math.max(this.totalPages - this.pageWindowSize + 1, 1);
    return Math.min(
      Math.max(this.currentPage - Math.floor(this.pageWindowSize / 2), 1),
      maxWindowStart
    );
  }

  get visiblePages(): number[] {
    if (this.totalPages <= 0) {
      return [];
    }

    const start = this.pageWindowStart;
    const end = Math.min(start + this.pageWindowSize - 1, this.totalPages);
    const pages: number[] = [];

    for (let page = start; page <= end; page++) {
      pages.push(page);
    }

    return pages;
  }

  get showLeadingEllipsis(): boolean {
    return this.pageWindowStart > 1;
  }

  get showTrailingEllipsis(): boolean {
    return (
      this.visiblePages.length > 0 &&
      this.visiblePages[this.visiblePages.length - 1] < this.totalPages
    );
  }

  get showBackControl(): boolean {
    return this.currentPage > 1;
  }

  get backTargetPage(): number {
    return this.currentPage - 1;
  }

  get showNextControl(): boolean {
    return this.currentPage < this.totalPages;
  }

  get nextTargetPage(): number {
    return this.currentPage + 1;
  }

  onPageChanged(event: PageEvent): void {
    this.pageChanged.emit(event);
  }

  onSortChanged(event: Sort): void {
    this.sortChanged.emit(event);
  }

  /** Material puts aria-sort on the sort header element, but assistive tech expects it on the th. */
  ariaSortFor(column: TableColumn<T>): string | null {
    if (!column.sortable) {
      return null;
    }

    if (this.sortActive !== (column.sortId || column.id) || !this.sortDirection) {
      return 'none';
    }

    return this.sortDirection === 'asc' ? 'ascending' : 'descending';
  }

  onRowClick(row: T): void {
    this.rowClicked.emit(row);
  }

  onPageClick(page: number): void {
    if (page < 1 || page > this.totalPages) {
      return;
    }

    const newPageIndex = page - 1;
    if (newPageIndex === this.pageIndex) {
      return;
    }

    this.pageChanged.emit({
      pageIndex: newPageIndex,
      pageSize: this.pageSize,
      length: this.length,
      previousPageIndex: this.pageIndex,
    });
  }

  onPageSizeSelectionChange(pageSize: number): void {
    if (pageSize === this.pageSize) {
      return;
    }

    this.pageChanged.emit({
      pageIndex: 0,
      pageSize,
      length: this.length,
      previousPageIndex: this.pageIndex,
    });
  }

  onRowEnter(row: T): void {
    this.hoveredRowId = this.rowId(row);
  }

  onRowLeave(): void {
    this.hoveredRowId = null;
  }

  onHeaderEnter(): void {
    this.headerHovered = true;
  }

  onHeaderLeave(): void {
    this.headerHovered = false;
  }

  isRowHovered(row: T): boolean {
    return this.hoveredRowId !== null && this.hoveredRowId === this.rowId(row);
  }

  isRowSelected(row: T): boolean {
    return this.selectedRowIds.has(this.rowId(row));
  }

  shouldShowRowSelection(row: T): boolean {
    return this.rowSelectable(row) && (this.isRowHovered(row) || this.isRowSelected(row));
  }

  shouldShowHeaderSelection(): boolean {
    return this.headerHovered || this.allVisibleRowsSelected;
  }

  onRowSelectionToggle(row: T, selected: boolean): void {
    this.rowSelectionChanged.emit({ row, selected });
  }

  onVisibleSelectionToggle(selected: boolean): void {
    this.visibleSelectionChanged.emit({
      rows: this.selectableVisibleRows,
      selected,
    });
  }

  selectionCheckboxId(row: T): string {
    const safeRowId = String(this.rowId(row)).replace(/[^A-Za-z0-9_-]/g, '-');
    return `${this.selectionIdPrefix}-${safeRowId}`;
  }

  onClearSelection(): void {
    this.selectionCleared.emit();
  }

  onSelectionAction(): void {
    if (!this.selectionDisabled) {
      this.selectionAction.emit();
    }
  }

  getText(column: TableColumn<T>, row: T): string {
    if (column.text) {
      return column.text(row);
    }

    if (column.field) {
      const value = this.resolvePath(row as any, column.field);
      return value == null ? '' : String(value);
    }

    return '';
  }

  getTextList(column: TableColumn<T>, row: T): string[] {
    return column.textList ? column.textList(row) : [];
  }

  getLink(column: TableColumn<T>, row: T): TableLink | null {
    return column.link ? column.link(row) : null;
  }

  getLinks(column: TableColumn<T>, row: T): TableLink[] {
    return column.links ? column.links(row) : [];
  }

  getIcons(column: TableColumn<T>, row: T): TableIcon[] {
    return column.icons ? column.icons(row) : [];
  }

  getIcon(column: TableColumn<T>, row: T): TableIcon | null {
    return column.icon ? column.icon(row) : null;
  }

  getStatus(column: TableColumn<T>, row: T): TableStatus | null {
    return column.status ? column.status(row) : null;
  }

  isFavourite(column: TableColumn<T>, row: T): boolean {
    return column.isFavourite ? column.isFavourite(row) : false;
  }

  isFavouriteDisabled(column: TableColumn<T>, row: T): boolean {
    return column.favouriteDisabled ? column.favouriteDisabled(row) : false;
  }

  favouriteLabel(column: TableColumn<T>, row: T): string {
    if (column.favouriteAriaLabel) {
      return column.favouriteAriaLabel(row);
    }

    return this.isFavourite(column, row) ? 'Remove from favourites' : 'Add to favourites';
  }

  shouldShowFavourite(column: TableColumn<T>, row: T): boolean {
    if (column.showFavourite && !column.showFavourite(row)) {
      return false;
    }

    return this.isFavourite(column, row) || this.isRowHovered(row);
  }

  getActions(column: TableColumn<T>, row: T): TableActionGroup {
    return column.actions ? column.actions(row) : {};
  }

  onFavouriteToggle(row: T, event: Event): void {
    event.stopPropagation();
    this.favouriteToggle.emit({ row, event });
  }

  onActionButtonClick(
    row: T,
    column: TableColumn<T>,
    event: Event,
    button?: TableButton | null
  ): void {
    event.stopPropagation();
    this.actionButtonClick.emit({
      row,
      columnId: column.id,
      actionId: button?.actionId,
    });
  }

  trackByIcon(index: number, icon: TableIcon): string {
    return `${icon.iconName || ''}|${icon.tooltip || ''}|${index}`;
  }

  // Cell accessors rebuild their arrays on every change detection run, so track by value
  // to stop Angular from recreating the elements and dropping keyboard focus.
  trackByLink(index: number, link: TableLink): string {
    return `${link.href || ''}|${link.label || ''}|${index}`;
  }

  trackByValue(index: number, value: string): string {
    return `${value}|${index}`;
  }

  isActionGroupColumn(column: TableColumn<T>): boolean {
    return column.type === 'actionGroup';
  }

  isActionColumnAlignedRight(column: TableColumn<T>): boolean {
    return this.isActionGroupColumn(column) || column.align === 'right';
  }

  private resolvePath(obj: Record<string, unknown>, field: string): unknown {
    return field.split('.').reduce((acc: unknown, key: string) => {
      if (acc == null || typeof acc !== 'object') {
        return undefined;
      }

      return (acc as Record<string, unknown>)[key];
    }, obj);
  }
}
